import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Watch,
  State,
  Fragment, Event, EventEmitter,
} from "@stencil/core";

import { StzhBreadcrumbItem, StzhBreadcrumbItemClickEvent } from "../../index";

import { StzhBreadcrumbLocalizedText } from "./stzh-breadcrumb.localization";

const ITEM_EXPAND_INDEX = 1;
const SHRINK_DIRECTION = 1; // 1 = removes item to the right / -1 = removes item to the left

/**
 *
 */
@Component({
  tag: "stzh-breadcrumb",
  styleUrl: "stzh-breadcrumb.scss",
  scoped: true
})
export class StzhBreadcrumb {
  /** Translation strings */
  @Prop() localization: StzhBreadcrumbLocalizedText;

  /** Variant */
  @Prop({ reflect: true }) variant: "default" | "teaser" = "default";

  /** Breadcrumb items */
  @Prop() items: StzhBreadcrumbItem[] | string = [];
  private _items: StzhBreadcrumbItem[];

  /** Whether all items are shown (true) or everything should fit into one line and not wrap (false, default) */
  @Prop({ mutable: true, reflect: true }) expanded: boolean = false;

  @Element() element: HTMLStzhBreadcrumbElement;

  @Watch("items")
  itemsWatcher(newValue: StzhBreadcrumbItem[] | string) {
    if (!newValue) {
      this._items = [];
    } else if (typeof newValue === "string") {
      try {
        this._items = JSON.parse(newValue);
      } catch (e) {
        this._items = [];
      }
    } else {
      this._items = newValue;
    }
  }

  @Watch("expanded")
  expandedWatcher() {
    this.updateContainer();
  }

  /** Item click event */
  @Event() stzhItemClick: EventEmitter<StzhBreadcrumbItemClickEvent>;

  @State() hasHiddenItems: boolean = false;

  private navElement: HTMLElement;
  private itemSizes: [HTMLElement, number][];

  private debounceResize: number;
  private resizeObserver: ResizeObserver;
  private debounceResizeItems: number;
  private resizeItemsObserver: ResizeObserver;

  private handleClick(item: StzhBreadcrumbItem, originalEvent: MouseEvent) {
    this.stzhItemClick.emit({
      component: "stzh-breadcrumb",
      item,
      originalEvent
    })
  }

  private updateContainer = () => {
    if (!this.navElement || !this.itemSizes) {
      return;
    }

    const containerWidth = this.navElement.clientWidth;

    const totalItemsWidthWithoutExpandItem = this.itemSizes
      .reduce((previous, item, index) => {
        if (index === ITEM_EXPAND_INDEX) {
          return previous;
        } else {
          return previous + item[1];
        }
      }, 0);

    if (totalItemsWidthWithoutExpandItem >= containerWidth && this.itemSizes.length > 2 && !this.expanded) {
      const hideItems = [];

      const expandItemWidth = this.itemSizes[ITEM_EXPAND_INDEX][1];
      let currentIndexToHide = ITEM_EXPAND_INDEX + SHRINK_DIRECTION;
      let totalItemsShownWidthWithoutExpandItem = totalItemsWidthWithoutExpandItem;

      do {
        const current = this.itemSizes[currentIndexToHide];
        hideItems.push(current[0]);
        totalItemsShownWidthWithoutExpandItem = totalItemsShownWidthWithoutExpandItem - current[1];
        currentIndexToHide = currentIndexToHide + SHRINK_DIRECTION;
      } while (
        totalItemsShownWidthWithoutExpandItem + expandItemWidth >= containerWidth
        && currentIndexToHide < this.itemSizes.length
      );

      this.hasHiddenItems = true;

      this.itemSizes.forEach(([item], index) => {
        if (index === ITEM_EXPAND_INDEX) {
          item.classList.remove('is-hidden');
        } else {
          item.classList.toggle('is-hidden', hideItems.includes(item));
        }
      });
    } else {
      this.hasHiddenItems = false;

      this.itemSizes.forEach(([item], index) => {
        if (index === ITEM_EXPAND_INDEX) {
          item.classList.add('is-hidden');
        } else {
          item.classList.remove('is-hidden');
        }
      });
    }
  }

  private updateItems = () => {
    if (!this.navElement) {
      return;
    }

    // TODO: current updates all item sizes, but could be optimized only to update really resized items with using passed ResizeObserver entries
    const items = Array.from(this.navElement.querySelectorAll('.stzh-breadcrumb__item')) as HTMLElement[];
    this.itemSizes = [];

    items.forEach((item) => {
      const { width } = item.getBoundingClientRect();
      this.itemSizes.push([item, width]);
    })

    this.updateContainer();
  }

  private handleContainerResize = () => {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.updateContainer);
  }

  private handleItemResize = () => {
    if (this.debounceResizeItems) {
      window.cancelAnimationFrame(this.debounceResizeItems);
    }

    this.debounceResizeItems = requestAnimationFrame(this.updateItems);
  }

  private handleExpandClick = () => {
    this.expanded = true;

    const itemLinks = Array.from(this.navElement.querySelectorAll('.stzh-breadcrumb__item-link')) as HTMLElement[];
    itemLinks[2].focus();
  }

  async componentWillLoad() {
    this.itemsWatcher(this.items);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "breadcrumb");
    }
  }

  componentDidRender() {
    this.resizeObserver.observe(this.navElement);

    const items = this.navElement.querySelectorAll('.stzh-breadcrumb__item');
    items.forEach((item) => {
      this.resizeItemsObserver.observe(item);
    });
  }

  connectedCallback() {
    this.resizeObserver = new ResizeObserver(this.handleContainerResize);
    this.resizeItemsObserver = new ResizeObserver(this.handleItemResize);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.resizeItemsObserver) {
      this.resizeItemsObserver.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-breadcrumb": true,
      "stzh-breadcrumb--is-expanded": this.expanded,
      "stzh-breadcrumb--has-hidden-items": this.hasHiddenItems,
      [`stzh-breadcrumb--${this.variant}`]: !!this.variant,
      "rs_skip": true // will prevent component being read by readspeaker
    };

    return (
      <Host>
        <div class={classes}>
          <nav
            ref={(el) => (this.navElement = el as HTMLElement)}
            class="stzh-breadcrumb__nav"
            role="navigation"
            aria-label={this.localization.title}
          >
            <ol class="stzh-breadcrumb__list">
              {this._items.map((item, index) =>
                <Fragment>
                  <li
                    class={{
                      "stzh-breadcrumb__item": true,
                      "is-last": index + 1 === this._items.length,
                      "is-first": index === 0,
                    }}
                  >
                    <a
                      aria-current={index + 1 === this._items.length ? "page" : null}
                      href={item.href}
                      onClick={(e) => this.handleClick(item, e)}
                      class={{
                        "stzh-breadcrumb__item-link": true,
                        "is-link": !!item.href
                      }}
                    >
                      {index === 0 ?
                        <stzh-icon
                          class="stzh-breadcrumb__item-icon"
                          name="home"
                        ></stzh-icon>
                      :
                        <stzh-icon
                          class="stzh-breadcrumb__item-icon is-separator"
                          name="angle-right"
                        ></stzh-icon>
                      }
                      <span class="stzh-breadcrumb__item-label" innerHTML={item.label}></span>
                    </a>
                  </li>
                  {index === 0 &&
                    <li class="stzh-breadcrumb__item is-expand">
                      <button class="stzh-breadcrumb__item-link" onClick={this.handleExpandClick}>
                        <stzh-icon
                          class="stzh-breadcrumb__item-icon is-separator"
                          name="angle-right"
                        ></stzh-icon>
                        <span class="stzh-breadcrumb__item-label">
                          <span aria-hdiden="true">
                            {this.localization.expandLabel}
                          </span>
                          <span class="stzh-breadcrumb__item-label-hidden">
                            {this.localization.expandHiddenLabel}
                          </span>
                        </span>
                      </button>
                    </li>
                  }
                </Fragment>
              )}
            </ol>
          </nav>
        </div>
      </Host>
    );
  }
}
